import { create } from 'zustand'

/**
 * historyStore — in-memory undo stack.
 *
 * Each snapshot captures the BEFORE state of both `goals` and `meta` at
 * the moment a user mutation was about to fire. On undo, we pop the most
 * recent snapshot and restore both to that state.
 *
 * Scope:
 *   - In-memory only (lost on reload). That matches how most apps handle
 *     undo — session-scoped, not persistent.
 *   - Depth: 30 entries. Beyond that, older entries are dropped.
 *   - Only user-driven mutations push snapshots. Heartbeats
 *     (useUpdateMetaLocal) do NOT push, so undo doesn't waste entries on
 *     every 20-second presence update.
 */
const MAX_HISTORY = 30

export const useHistoryStore = create((set, get) => ({
  history: [],
  redoStack: [],

  /**
   * Push a new undo snapshot.
   *
   * @param {string} label Short description ("Edited current", "Deleted action item", etc.)
   * @param {Array|null} goalsBefore  The full goals array BEFORE the mutation.
   *   Optional. Only populated for structural CRUD mutations that don't
   *   map to a single field revert. Passing `undefined` means "this
   *   mutation is covered by goalFieldRevert — don't attempt a full-
   *   state goals restore on undo" (avoids clobbering concurrent edits).
   * @param {Object|null} metaBefore  The meta object BEFORE the mutation.
   *   Used for undo of meta-level changes; safe because the worker's
   *   /meta POST has a PRESERVED list that blocks clobber on weekly
   *   content slices.
   * @param {Object|null} goalFieldRevert  For single-field goal/sub-goal
   *   edits, the targeted revert payload: { pillarName, goalIndex,
   *   subgoalIndex?, field, value }. When present, undo applies this
   *   via the partial-update /goals/field endpoint — atomic, concurrent-
   *   safe, and never overwrites teammates' edits to other fields.
   */
  pushSnapshot: (label, goalsBefore, metaBefore, goalFieldRevert) => {
    set((s) => ({
      history: [
        ...s.history.slice(-(MAX_HISTORY - 1)),
        {
          id: Math.random().toString(36).slice(2),
          at: Date.now(),
          label,
          goalsBefore,
          metaBefore,
          goalFieldRevert,
        },
      ],
      // New action clears any pending redo
      redoStack: [],
    }))
  },

  /**
   * Pop the most recent snapshot. Returns it, or null if empty.
   * Caller is responsible for applying the snapshot to server + cache.
   */
  popSnapshot: () => {
    const { history } = get()
    if (history.length === 0) return null
    const last = history[history.length - 1]
    set((s) => ({
      history: s.history.slice(0, -1),
      redoStack: [...s.redoStack, last],
    }))
    return last
  },

  clear: () => set({ history: [], redoStack: [] }),

  canUndo: () => get().history.length > 0,
}))
